import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { 
  DocumentTextIcon, 
  PencilIcon, 
  PrinterIcon,
  CheckCircleIcon,
  ClockIcon,
  ClockIcon as HistoryIcon,
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { errorStateVariantFromError } from "../../../../components/utils/errorStateUtils";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { getHseDocument, getHseDocuments, getDocumentVersions, approveDocumentVersion, submitDocumentForApproval, printDocument, getVersionApprovals, acknowledgeDocumentVersionRead, updateHseDocument, refreshChecklistDraftSnapshot } from "../../../../api/hse/documents";
import { DocumentAccessDialog } from "../../../../components/dashboard/documents/DocumentAccessDialog";
import { ReadRequirementNotice } from "../../../../components/dashboard/hse/compliance/ReadRequirementNotice";
import { notifyHseReadChanged } from "../../../../api/hse/documentCompliance";
import {
  ApproveRevisionDialog,
  hasEarlierApprovedVersion,
} from "../../../../components/dashboard/hse/compliance/ApproveRevisionDialog";
import { DocumentFileContent } from "./DocumentFileContent";
import NewDocumentFormDialog from "../../../../components/dashboard/document-forms/NewDocumentFormDialog";
import NewChecklistFromHseDialog from "../../../../components/dashboard/quality/checklists/NewChecklistFromHseDialog";
import HseChecklistTemplatePreview from "../../../../components/dashboard/quality/checklists/HseChecklistTemplatePreview";
import { formatHseDocumentDisplayNumber } from "../../../../utils/documentStructureUtils";
import "../../../../components/utils/PromangRichTextEditor.css";

function pickPreferredVersion(versionsList, revStatus) {
  if (!versionsList?.length) return null;

  const revisionStatus = revStatus ?? {};

  if (revisionStatus.draft_revision_in_progress) {
    if (revisionStatus.draft_version_id) {
      const byId = versionsList.find((v) => v.id === revisionStatus.draft_version_id);
      if (byId) return byId;
    }
    const approvedVersionNumber =
      versionsList.find((v) => v.is_approved || v.status === "approved")?.version || 0;
    const openRevision = versionsList.find(
      (v) =>
        (v.status === "draft" || v.status === "pending_approval") &&
        (!approvedVersionNumber || v.version > approvedVersionNumber)
    );
    if (openRevision) return openRevision;
  }

  const approvedVersion = versionsList.find(
    (v) => v.status === "approved" && v.is_approved
  );
  if (approvedVersion) return approvedVersion;

  return versionsList[0];
}

function applyDocumentContentFallback(doc, versionsList, selectVersion) {
  const approvedFromList =
    versionsList.find((v) => v.status === "approved" && v.is_approved) ||
    (doc?.approved_version_id
      ? versionsList.find((v) => v.id === doc.approved_version_id)
      : null);

  if (approvedFromList) {
    selectVersion(approvedFromList);
    return;
  }

  if (doc?.approved_version_id) {
    selectVersion({
      id: doc.approved_version_id,
      version: doc.current_version,
      status: doc.status,
      content: doc.content,
      content_type: doc.content_type,
      attached_document_id: doc.attached_document_id,
      attached_document: doc.attached_document,
      is_approved: doc.status === "approved",
    });
    return;
  }

  if (doc?.content || doc?.attached_document_id) {
    selectVersion({
      id: null,
      version: doc.current_version,
      status: doc.status,
      content: doc.content,
      content_type: doc.content_type,
      attached_document_id: doc.attached_document_id,
      attached_document: doc.attached_document,
      is_approved: doc.status === "approved",
    });
  }
}

/**
 * HSE/KS Document View - View document details with version history and print
 */
export const HseKsDocumentView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [document, setDocument] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null); // Selected version to display
  const [displayContent, setDisplayContent] = useState(null); // Content of selected version
  const [displayAttachedDocument, setDisplayAttachedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorVariant, setErrorVariant] = useState("load_error");
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [approvingVersion, setApprovingVersion] = useState(null);
  const [printingVersionId, setPrintingVersionId] = useState(null);
  const [myAcknowledged, setMyAcknowledged] = useState(null);
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [chapterSiblingDocuments, setChapterSiblingDocuments] = useState([]);
  const [chapterSiblingsExpanded, setChapterSiblingsExpanded] = useState(true);
  const [showFillFormDialog, setShowFillFormDialog] = useState(false);
  const [showCreateChecklistDialog, setShowCreateChecklistDialog] = useState(false);
  const [creatingChecklistRevision, setCreatingChecklistRevision] = useState(false);
  const [refreshingChecklistDraft, setRefreshingChecklistDraft] = useState(false);
  
  // All hooks must be called before any conditional logic
  const canEdit = useUserHasAccess({ role: "hse_ks_documents", action: "update" });
  const canApproveRole = useUserHasAccess({ role: "hse_ks_documents_approve", action: "approve" });

  const documentReturnPath = `/dashboard/hse-ks/documents/${id}`;
  const getChecklistTypeId = () =>
    document?.checklist_type_revision_status?.checklist_type_id ||
    document?.checklist_template?.id;
  const getChecklistTypeEditPath = () => {
    const typeId = getChecklistTypeId();
    return typeId
      ? `/dashboard/hse-ks/settings/checklistType/${typeId}?returnTo=${encodeURIComponent(documentReturnPath)}`
      : null;
  };
  const isChecklistGatewayDocument = () => Boolean(getChecklistTypeId());
  
  // Conditional logic after hooks
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const canApprove = isAdmin || canApproveRole;

  useEffect(() => {
    loadDocumentAndVersions({ reselectVersion: true });
    setChapterSiblingsExpanded(true);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id, location.key]);

  const resolveVersionAttachment = (version) =>
    version?.attached_document ||
    (version?.attached_document_id ? { id: version.attached_document_id } : null);

  const selectVersion = useCallback((version) => {
    if (!version) return;
    setSelectedVersionId(version.id);
    setDisplayContent(version.content);
    setDisplayAttachedDocument(resolveVersionAttachment(version));
  }, []);

  const loadDocumentAndVersions = async ({ reselectVersion = true } = {}) => {
    if (reselectVersion) {
      setLoading(true);
    }
    setError(false);

    let doc = null;
    try {
      const docResponse = await getHseDocument(id);
      doc = docResponse?.document ?? null;
      if (!doc?.id) {
        const missing = new Error("Document payload missing");
        missing.status = 404;
        throw missing;
      }
      setDocument(doc);
    } catch (err) {
      console.error("Error loading document:", err);
      const variant = errorStateVariantFromError(err);
      setError(true);
      setErrorVariant(variant);
      if (variant === "load_error") {
        toast.error(t("common.error_state.load_error.title"));
      }
      if (reselectVersion) {
        setLoading(false);
      }
      return;
    }

    let versionsList = [];
    try {
      const versionsResponse = await getDocumentVersions(id);
      versionsList = versionsResponse?.versions ?? [];
      setVersions(versionsList);
    } catch (err) {
      console.error("Error loading document versions:", err);
      setVersions([]);
    }

    if (reselectVersion) {
      const preferred = pickPreferredVersion(
        versionsList,
        doc?.checklist_type_revision_status
      );
      if (preferred) {
        selectVersion(preferred);
      } else {
        applyDocumentContentFallback(doc, versionsList, selectVersion);
      }
    } else if (selectedVersionId) {
      const current = versionsList.find((v) => v.id === selectedVersionId);
      if (current) {
        selectVersion(current);
      }
    }

    if (reselectVersion) {
      setLoading(false);
    }
  };

  const findOpenRevision = useCallback(() => {
    const approvedVersionNumber =
      versions.find((v) => v.is_approved || v.status === "approved")?.version || 0;
    return versions.find(
      (v) =>
        (v.status === "draft" || v.status === "pending_approval") &&
        (!approvedVersionNumber || v.version > approvedVersionNumber)
    );
  }, [versions]);

  const handleViewOpenRevision = useCallback(() => {
    const openRevision = findOpenRevision();
    if (openRevision) {
      selectVersion(openRevision);
      return;
    }
    const draftVersion = versions.find((v) => v.status === "draft");
    if (draftVersion) {
      selectVersion(draftVersion);
    }
  }, [findOpenRevision, selectVersion, versions]);

  useEffect(() => {
    if (loading || location.hash !== "#document-revisions") return undefined;
    const timer = window.setTimeout(() => {
      window.document.getElementById("document-revisions")?.scrollIntoView({ behavior: "smooth" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [loading, location.hash]);

  useEffect(() => {
    const loadChapterSiblings = async () => {
      const chapterId = document?.chapter?.id;
      if (!chapterId) {
        setChapterSiblingDocuments([]);
        return;
      }

      try {
        const filters = {};
        const categoryCode = document.category?.code || document.category_code;
        if (categoryCode) {
          filters.category = categoryCode;
        }
        if (document.chapter?.number != null) {
          filters.chapter = String(document.chapter.number);
        }

        const response = await getHseDocuments(filters);
        const siblings = (response.documents || [])
          .filter(
            (doc) =>
              doc.chapter?.id === chapterId && String(doc.id) !== String(id)
          )
          .sort((a, b) => {
            const na = String(a.document_number || a.title || "").toLowerCase();
            const nb = String(b.document_number || b.title || "").toLowerCase();
            return na.localeCompare(nb, "nb", { numeric: true });
          });
        setChapterSiblingDocuments(siblings);
      } catch (err) {
        console.error("Error loading chapter sibling documents:", err);
        setChapterSiblingDocuments([]);
      }
    };

    if (document?.chapter?.id) {
      loadChapterSiblings();
    } else {
      setChapterSiblingDocuments([]);
    }
  }, [
    document?.chapter?.id,
    document?.chapter?.number,
    document?.category?.code,
    document?.category_code,
    id,
  ]);

  // Update display content when selected version changes
  useEffect(() => {
    if (selectedVersionId && versions.length > 0) {
      const selectedVersion = versions.find(v => v.id === selectedVersionId);
      if (selectedVersion) {
        setDisplayContent(selectedVersion.content);
        setDisplayAttachedDocument(resolveVersionAttachment(selectedVersion));
      }
    }
  }, [selectedVersionId, versions]);

  useEffect(() => {
    let cancelled = false;
    const loadAck = async () => {
      if (!id || !selectedVersionId || !currentUser?.id) {
        setMyAcknowledged(null);
        return;
      }
      // Route id changes before document/versions state is reset — do not ask
      // the new document for the previous document's version (prod 404).
      if (!document?.id || String(document.id) !== String(id)) {
        setMyAcknowledged(null);
        return;
      }
      const v = versions.find((x) => String(x.id) === String(selectedVersionId));
      if (!v || v.status !== "approved") {
        setMyAcknowledged(null);
        return;
      }
      try {
        const res = await getVersionApprovals(id, selectedVersionId);
        if (cancelled) return;
        const mine = res.approvals?.find((a) => a.user_id === currentUser.id);
        // read_understood_at = explicit read receipt; approved === true may be legacy read-ack or workflow vote on published versions
        setMyAcknowledged(!!mine?.read_understood_at || mine?.approved === true);
      } catch {
        if (!cancelled) setMyAcknowledged(null);
      }
    };
    loadAck();
    return () => {
      cancelled = true;
    };
  }, [id, document?.id, selectedVersionId, versions, currentUser?.id]);

  const refreshDocumentView = async ({ reselectVersion = false } = {}) => {
    await loadDocumentAndVersions({ reselectVersion });
  };

  const handlePrint = async (versionId = null) => {
    // Prevent multiple clicks
    if (printingVersionId !== null) {
      return;
    }
    
    try {
      setPrintingVersionId(versionId);
      const response = await printDocument(id, versionId, 'document');
      if (response.pdf_url) {
        window.open(response.pdf_url, '_blank');
        toast.success(t("Dokument skrives ut..."));
      } else {
        toast.error(t("Kunne ikke generere PDF"));
      }
    } catch (err) {
      console.error("Error printing document:", err);
      const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke skrive ut dokument");
      toast.error(errorMessage);
    } finally {
      setPrintingVersionId(null);
    }
  };

  const handleCreateRevision = async () => {
    try {
      const openRevision = findOpenRevision();
      if (openRevision) {
        selectVersion(openRevision);
        return;
      }
      if (isChecklistGatewayDocument()) {
        await handleCreateRevisionAndEditChecklist();
      } else {
        navigate(`/dashboard/hse-ks/documents/${id}/edit?revision=true`);
      }
    } catch (err) {
      toast.error(t("Kunne ikke opprette revisjon"));
    }
  };

  const handleCreateRevisionAndEditChecklist = async () => {
    const checklistTypeEditPath = getChecklistTypeEditPath();
    if (!canEdit || !checklistTypeEditPath) return;

    const approvedVersionNumber =
      versions.find((v) => v.is_approved || v.status === "approved")?.version || 0;
    const openRevision = versions.find(
      (v) =>
        (v.status === "draft" || v.status === "pending_approval") &&
        v.version > approvedVersionNumber
    );
    if (openRevision) {
      navigate(checklistTypeEditPath);
      return;
    }

    try {
      setCreatingChecklistRevision(true);
      await updateHseDocument(
        id,
        {},
        {
          create_revision: true,
          change_summary: t("quality.checklist.type.hse.checklist_revision_summary"),
        }
      );
      await refreshDocumentView({ reselectVersion: true });
      toast.success(t("quality.checklist.type.hse.revision_created"));
      navigate(checklistTypeEditPath);
    } catch (err) {
      console.error("Error creating checklist revision:", err);
      const errorMessage =
        err.response?.data?.errors?.[0] ||
        t("quality.checklist.type.hse.revision_create_failed");
      toast.error(errorMessage);
    } finally {
      setCreatingChecklistRevision(false);
    }
  };

  const handleGoToEditChecklist = () => {
    const checklistTypeEditPath = getChecklistTypeEditPath();
    if (checklistTypeEditPath) {
      navigate(checklistTypeEditPath);
    }
  };

  const handleRefreshChecklistDraft = async () => {
    try {
      setRefreshingChecklistDraft(true);
      await refreshChecklistDraftSnapshot(id);
      toast.success(t("quality.checklist.type.hse.draft_snapshot_refreshed"));
      await refreshDocumentView({ reselectVersion: true });
    } catch (err) {
      console.error("Error refreshing checklist draft:", err);
      const errorMessage =
        err.response?.data?.errors?.[0] ||
        t("quality.checklist.type.hse.draft_snapshot_refresh_failed");
      toast.error(errorMessage);
    } finally {
      setRefreshingChecklistDraft(false);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      await submitDocumentForApproval(id);
      toast.success(t("Dokument sendt til godkjenning"));
      await refreshDocumentView();
    } catch (err) {
      console.error("Error submitting for approval:", err);
      const errorMessage = err.response?.data?.errors?.[0] || t("Kunne ikke sende dokument til godkjenning");
      toast.error(errorMessage);
    }
  };

  const handleAcknowledgeRead = async () => {
    if (!selectedVersionId || ackSubmitting) return;
    try {
      setAckSubmitting(true);
      await acknowledgeDocumentVersionRead(id, selectedVersionId, null);
      toast.success(t("hse.document.acknowledge_read_success"));
      notifyHseReadChanged();
      setMyAcknowledged(true);
      await refreshDocumentView();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.errors?.[0] || t("hse.document.acknowledge_read_error");
      toast.error(msg);
    } finally {
      setAckSubmitting(false);
    }
  };

  const [approveDialogVersion, setApproveDialogVersion] = useState(null);
  const [approveDialogError, setApproveDialogError] = useState(null);

  const approveVersionNow = async (versionId, options = {}) => {
    try {
      setApprovingVersion(versionId);
      setApproveDialogError(null);
      await approveDocumentVersion(id, versionId, null, options);
      toast.success(t("Versjon godkjent"));
      setApproveDialogVersion(null);
      await refreshDocumentView();
    } catch (err) {
      console.error("Error approving version:", err);
      const message = err.response?.data?.errors?.[0] || t("Kunne ikke godkjenne versjon");
      if (approveDialogVersion) setApproveDialogError(message);
      else toast.error(message);
    } finally {
      setApprovingVersion(null);
    }
  };

  // A revision of an already approved document asks whether readers must confirm again.
  const handleApproveVersion = async (versionId) => {
    const version = versions.find((v) => v.id === versionId);
    if (version && hasEarlierApprovedVersion(versions, version.version)) {
      setApproveDialogError(null);
      setApproveDialogVersion(version);
      return;
    }
    await approveVersionNow(versionId);
  };

  const DocReadStatusBadge = ({ doc }) => {
    const st = doc?.current_user_read_status;
    if (st?.exempt_as_creator) {
      return (
        <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
          {t("employee_handbook.read_status_creator_exempt")}
        </span>
      );
    }
    if (st?.acknowledged_read_understood) {
      return (
        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
          <CheckCircleIcon className="h-3.5 w-3.5" />
          {t("employee_handbook.read_status_acknowledged")}
        </span>
      );
    }
    return (
      <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900">
        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
        {t("employee_handbook.read_status_pending")}
      </span>
    );
  };

  const renderChapterSiblingDocuments = () => {
    if (chapterSiblingDocuments.length === 0) return null;

    const chapter = document?.chapter;

    return (
      <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50/80 shadow sm:rounded-lg">
        <button
          type="button"
          onClick={() => setChapterSiblingsExpanded((expanded) => !expanded)}
          className="flex w-full items-start justify-between gap-2 p-4 text-left sm:px-6"
          aria-expanded={chapterSiblingsExpanded}
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              {t("employee_handbook.other_chapter_documents")}
            </p>
            {chapter ? (
              <p className="mt-0.5 text-xs text-slate-600">
                {chapter.number ? `#${chapter.number} · ` : ""}
                {chapter.title}
              </p>
            ) : null}
            <p className="mt-1 text-xs text-slate-500">
              {t("employee_handbook.other_chapter_documents_hint")}
            </p>
          </div>
          {chapterSiblingsExpanded ? (
            <ChevronUpIcon className="h-5 w-5 shrink-0 text-slate-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 shrink-0 text-slate-400" />
          )}
        </button>
        {chapterSiblingsExpanded ? (
          <ul className="divide-y divide-slate-200 border-t border-slate-200 bg-white sm:rounded-b-lg">
            {chapterSiblingDocuments.map((doc) => (
              <li key={doc.id}>
                <Link
                  to={`/dashboard/hse-ks/documents/${doc.id}`}
                  className="flex items-start gap-3 p-4 transition-colors hover:bg-slate-50 sm:px-6"
                >
                  <DocumentTextIcon className="h-4 w-4 shrink-0 text-blue-600" />
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-medium text-slate-900">
                      {doc.title}
                    </p>
                    {doc.document_type_display ? (
                      <p className="mt-0.5 text-xs text-slate-500">
                        {doc.document_type_display}
                      </p>
                    ) : null}
                    {doc.document_number ? (
                      <p className="mt-0.5 font-mono text-[11px] text-slate-400">
                        {doc.full_document_number || doc.document_number}
                      </p>
                    ) : null}
                    {doc.status_display ? (
                      <p className="mt-0.5 text-xs text-slate-500">{doc.status_display}</p>
                    ) : null}
                  </div>
                  {doc.current_user_read_status ? (
                    <DocReadStatusBadge doc={doc} />
                  ) : null}
                  <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400" />
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  };


  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error || !document) {
    return (
      <ResourceError
        variant={!document && !error ? "not_found" : errorVariant}
        errors={
          errorVariant === "load_error" && error
            ? [t("common.error_state.load_error.title")]
            : undefined
        }
        onRetry={error ? () => loadDocumentAndVersions({ reselectVersion: true }) : undefined}
        backTo="/dashboard/hse-ks/documents"
      />
    );
  }

  // Get selected version to check its status
  const selectedVersion = versions.find(v => v.id === selectedVersionId);
  const selectedVersionStatus = selectedVersion?.status || document?.status;
  const isSelectedVersionApproved = selectedVersion?.is_approved || selectedVersion?.status === 'approved';
  
  // Check if document can be approved (pending approval and user has permission)
  const canApproveDocument = canApprove && document.status === 'pending_approval';
  // Check if document can be submitted for approval
  // Don't show if selected version is already approved
  // Only show if selected version is draft or if no version is selected and document status allows it
  const canSubmitForApproval = canEdit && 
    !isSelectedVersionApproved &&
    document.status !== 'pending_approval' && 
    (selectedVersionStatus === 'draft' || 
     (!selectedVersionId && (document.status === 'draft' || document.status === 'approved' || !document.status || document.status === null)));

  const fillableTemplate = document.fillable_template;
  const canFillForm = Boolean(fillableTemplate?.id && fillableTemplate.active !== false);
  const selectedVersionForTemplate = versions.find((v) => v.id === selectedVersionId);
  const checklistTemplate =
    selectedVersionForTemplate?.checklist_template ??
    (selectedVersionId ? null : document.checklist_template);
  const checklistPreviewKey = [
    selectedVersionId,
    checklistTemplate?.synced_at,
    checklistTemplate?.checklist_type_updated_at,
    checklistTemplate?.items?.length,
  ].join("-");
  const canCreateChecklist = Boolean(checklistTemplate?.id && checklistTemplate.active !== false);
  const isFormGateway = canFillForm || canCreateChecklist;
  const revStatus = document.checklist_type_revision_status || {};
  const checklistTypeId = revStatus.checklist_type_id || checklistTemplate?.id;
  const checklistTypeEditPath = checklistTypeId
    ? `/dashboard/hse-ks/settings/checklistType/${checklistTypeId}?returnTo=${encodeURIComponent(`/dashboard/hse-ks/documents/${id}`)}`
    : null;
  const checklistTypePendingRevision = revStatus.pending_revision === true;
  const checklistDraftInProgress = revStatus.draft_revision_in_progress === true;
  const checklistDraftSnapshotStale = revStatus.draft_snapshot_stale === true;

  const resolveDocumentContent = () => {
    if (checklistTemplate?.id) return null;

    const selectedVersion = versions.find((v) => v.id === selectedVersionId);
    const fromVersion = displayContent ?? selectedVersion?.content;
    if (fromVersion && String(fromVersion).trim()) return fromVersion;
    if (document?.content && String(document.content).trim()) return document.content;
    return null;
  };

  // Check if current selected version can be printed
  const canPrintCurrentVersion = isFormGateway || (selectedVersionId 
    ? versions.find(v => v.id === selectedVersionId)?.status === 'approved' || isAdmin
    : document?.has_approved_version || isAdmin);
  
  const headerActions = [
    ...(canCreateChecklist ? [{
      component: null,
      label: t("quality.checklist.type.hse.create_checklist"),
      action: () => setShowCreateChecklistDialog(true),
      icon: DocumentTextIcon,
      textColor: "text-emerald-700",
      bgColor: "bg-emerald-50",
      hidden: false,
      menu: false,
    }] : []),
    ...(canFillForm ? [{
      component: null,
      label: t("documentForms.hse.fill_form"),
      action: () => setShowFillFormDialog(true),
      icon: DocumentTextIcon,
      textColor: "text-indigo-700",
      bgColor: "bg-indigo-50",
      hidden: false,
      menu: false,
    }] : []),
    {
      component: null,
      label: printingVersionId !== null ? t("Genererer PDF...") : t("Skriv ut"),
      action: () => handlePrint(selectedVersionId),
      icon: PrinterIcon,
      textColor: "text-gray-700",
      bgColor: "bg-white",
      hidden: false,
      disabled: !canPrintCurrentVersion || printingVersionId !== null,
      loading: printingVersionId !== null,
      menu: true,
    },
    ...(canSubmitForApproval ? [{
      component: null,
      label: t("Send til godkjenning"),
      action: handleSubmitForApproval,
      icon: PaperAirplaneIcon,
      textColor: "text-blue-700",
      bgColor: "bg-blue-50",
      hidden: false,
      menu: true,
    }] : []),
    ...(canApproveDocument ? [{
      component: null,
      label: t("hse.actions.approve"),
      action: () => {
        navigate(`/dashboard/hse-ks/documents/${id}/approve`);
      },
      icon: CheckCircleIcon,
      textColor: "text-green-700",
      bgColor: "bg-green-50",
      hidden: false,
      menu: true,
    }] : []),
    ...(versions.length > 0 ? [{
      component: null,
      label: t("hse.actions.revisions"),
      action: () => {
        window.document.getElementById("document-revisions")?.scrollIntoView({ behavior: "smooth" });
      },
      icon: HistoryIcon,
      textColor: "text-gray-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    }] : []),
    ...(isAdmin ? [{
      component: null,
      label: t("hseCompliance.dialog.menu_label"),
      action: () => setShowAccessDialog(true),
      icon: UserGroupIcon,
      textColor: "text-gray-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    }] : []),
  ];

  return (
    <HsePageChrome
      title={document.title || `Dokument #${id}`}
      subtitle="Dokumentdetaljer med versjonshistorikk"
      backTo="/dashboard/hse-ks/documents"
      actions={headerActions}
      mainButton={canEdit ? {
        label: canCreateChecklist
          ? document.status === "approved"
            ? t("Ny revisjon")
            : t("quality.checklist.type.hse.edit_checklist")
          : document.status === "approved"
            ? t("Ny revisjon")
            : t("Rediger"),
        action: canCreateChecklist
          ? document.status === "approved"
            ? handleCreateRevision
            : handleGoToEditChecklist
          : document.status === "approved"
            ? handleCreateRevision
            : () => {
                const draftVersion = versions.find((v) => v.status === "draft");
                if (draftVersion) {
                  handleViewOpenRevision();
                } else {
                  navigate(`/dashboard/hse-ks/documents/${id}/edit`);
                }
              },
        path: null,
        color: "",
        icon: document.status === "approved" ? DocumentArrowDownIcon : PencilIcon,
      } : null}
      backButton={{
        label: t("Tilbake"),
        action: () => navigate("/dashboard/hse-ks/documents"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mt-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {canEdit && canCreateChecklist && checklistDraftInProgress ? (
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <ClockIcon className="h-5 w-5 shrink-0 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900">
                    {t("quality.checklist.type.hse.revision_in_progress_title")}
                  </p>
                  <p className="mt-1 text-sm text-blue-800">
                    {checklistDraftSnapshotStale
                      ? t("quality.checklist.type.hse.revision_in_progress_stale_hint")
                      : t("quality.checklist.type.hse.revision_in_progress_hint")}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap shrink-0 gap-2">
                <button
                  type="button"
                  onClick={handleGoToEditChecklist}
                  disabled={!checklistTypeEditPath}
                  className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {t("quality.checklist.type.hse.edit_checklist")}
                </button>
                {checklistDraftSnapshotStale ? (
                  <button
                    type="button"
                    onClick={handleRefreshChecklistDraft}
                    disabled={refreshingChecklistDraft}
                    className="inline-flex items-center justify-center rounded-md border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                  >
                    {refreshingChecklistDraft
                      ? t("Loading")
                      : t("quality.checklist.type.hse.refresh_draft_revision")}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={handleViewOpenRevision}
                  className="inline-flex items-center justify-center rounded-md border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100"
                >
                  {t("quality.checklist.type.hse.view_draft_revision")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {checklistTypePendingRevision && canEdit && canCreateChecklist && !checklistDraftInProgress ? (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">
                    {t("quality.checklist.type.hse.updated_checklist_available")}
                  </p>
                  <p className="mt-1 text-sm text-amber-800">
                    {t("quality.checklist.type.hse.updated_checklist_available_hint")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCreateRevisionAndEditChecklist}
                disabled={creatingChecklistRevision || !checklistTypeEditPath}
                className="inline-flex shrink-0 items-center justify-center rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {creatingChecklistRevision
                  ? t("Loading")
                  : t("quality.checklist.type.hse.create_revision_and_edit_checklist")}
              </button>
            </div>
          </div>
        ) : null}
        {canCreateChecklist ? (
          <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-emerald-900">
                  {t("quality.checklist.type.hse.checklist_template_title")}
                </p>
                <p className="mt-1 text-sm text-emerald-800">
                  {t("quality.checklist.type.hse.checklist_template_description")}
                </p>
              </div>
              <div className="flex flex-wrap shrink-0 gap-2">
                {canEdit && checklistTypeEditPath ? (
                  <button
                    type="button"
                    onClick={handleGoToEditChecklist}
                    className="inline-flex items-center justify-center rounded-md border border-emerald-300 bg-white px-4 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
                  >
                    {t("quality.checklist.type.hse.edit_checklist")}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setShowCreateChecklistDialog(true)}
                  className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                >
                  {t("quality.checklist.type.hse.create_checklist")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {canFillForm ? (
          <div className="mb-6 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-indigo-900">
                  {t("documentForms.hse.fillable_template_title")}
                </p>
                <p className="mt-1 text-sm text-indigo-800">
                  {t("documentForms.hse.fillable_template_description")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowFillFormDialog(true)}
                className="inline-flex shrink-0 items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                {t("documentForms.hse.fill_form")}
              </button>
            </div>
          </div>
        ) : null}
        {/* Document Info - Compact */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-4 sm:px-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  <DocumentTextIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <h3 className="text-lg leading-6 font-medium text-gray-900 truncate">
                    {document.title}
                  </h3>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 ml-8">
                  {formatHseDocumentDisplayNumber(document) ? (
                    <span className="font-mono font-medium text-gray-800">
                      {formatHseDocumentDisplayNumber(document)}
                    </span>
                  ) : null}
                  <span className="font-medium">{document.document_type_display || document.document_type}</span>
                  <span>{t("Versjon")} {document.current_version}</span>
                  {document.category && (
                    <span>{document.category_display || document.category.name}</span>
                  )}
                  {document.chapter && (
                    <span>{document.chapter.number} - {document.chapter.title}</span>
                  )}
                  {document.discipline && (
                    <span>{document.discipline.name}</span>
                  )}
                </div>
                {document.description && (
                  <p className="mt-2 text-sm text-gray-600 ml-8 line-clamp-2">
                    {document.description}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                {document.status === "approved" ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                    {document.status_display || t("Godkjent")}
                  </span>
                ) : document.status === "pending_approval" ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {document.status_display || t("Venter på godkjenning")}
                  </span>
                ) : document.status ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {document.status_display || document.status}
                  </span>
                ) : null}
              </div>
            </div>
            {(document.created_by || document.approved_by) && (
              <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 ml-8">
                {document.created_by && (
                  <span>{t("Opprettet av")} {document.created_by.name} {document.created_at && `• ${new Date(document.created_at).toLocaleDateString()}`}</span>
                )}
                {document.approved_by && (
                  <span>{t("Godkjent av")} {document.approved_by.name} {document.updated_at && `• ${new Date(document.updated_at).toLocaleDateString()}`}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {renderChapterSiblingDocuments()}

        {/* Document Content */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              {canCreateChecklist
                ? t("quality.checklist.type.hse.checklist_template_title")
                : t("Innhold")}
            </h3>
            {selectedVersionId && versions.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  {t("Viser")}: {versions.find(v => v.id === selectedVersionId)?.status === 'approved' 
                    ? t("Godkjent versjon") 
                    : versions.find(v => v.id === selectedVersionId)?.status === 'draft'
                    ? t("Kladd versjon")
                    : versions.find(v => v.id === selectedVersionId)?.status === 'pending_approval'
                    ? t("Versjon venter på godkjenning")
                    : t("Versjon")} {versions.find(v => v.id === selectedVersionId)?.version}
                </p>
                {versions.find((v) => v.id === selectedVersionId)?.status === "approved" && currentUser?.id && (
                  <div className="mt-3">
                    <ReadRequirementNotice
                      documentId={id}
                      status={{
                        ...(document?.current_user_read_status || {}),
                        acknowledged_read_understood:
                          myAcknowledged === true ||
                          Boolean(document?.current_user_read_status?.acknowledged_read_understood),
                      }}
                      onAcknowledge={handleAcknowledgeRead}
                      acknowledging={ackSubmitting || myAcknowledged === null}
                      onRequirementChange={() => refreshDocumentView()}
                    />
                  </div>
                )}
              </div>
            )}
            <div className="prose max-w-none overflow-x-auto promang-rich-text-content">
              {canCreateChecklist && checklistTemplate ? (
                <>
                  {selectedVersionForTemplate &&
                  (selectedVersionForTemplate.status === "draft" ||
                    selectedVersionForTemplate.status === "pending_approval") ? (
                    <p className="mb-3 text-xs font-medium text-blue-800 not-prose">
                      {t("quality.checklist.type.hse.preview_draft_version", {
                        version: selectedVersionForTemplate.version,
                        defaultValue: "Forhåndsvisning av kladd revisjon {{version}}",
                      })}
                    </p>
                  ) : null}
                  <HseChecklistTemplatePreview
                    key={checklistPreviewKey}
                    template={checklistTemplate}
                  />
                </>
              ) : (
              (() => {
                const selectedVersion = versions.find(v => v.id === selectedVersionId);
                const contentType =
                  selectedVersion?.content_type ||
                  document.content_type ||
                  "text";
                const attachedDocument =
                  displayAttachedDocument ||
                  selectedVersion?.attached_document ||
                  document.attached_document ||
                  null;
                const attachedDocumentId =
                  attachedDocument?.id ||
                  selectedVersion?.attached_document_id ||
                  document.attached_document_id ||
                  null;
                const hasFileContent =
                  contentType === "file" || !!attachedDocumentId;

                const htmlContent = resolveDocumentContent();
                if (htmlContent) {
                  return (
                    <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                  );
                }

                if (hasFileContent) {
                  return (
                    <DocumentFileContent
                      attachedDocument={attachedDocument}
                      attachedDocumentId={attachedDocumentId}
                    />
                  );
                }

                return <p className="text-gray-500">{t("Ingen innhold")}</p>;
              })()
              )}
            </div>
          </div>
        </div>

        {/* Version History */}
        <div id="document-revisions" className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <HistoryIcon className="h-5 w-5 mr-2" />
                {t("Versjonshistorikk")}
              </h3>
              <RoleOnly role="hse_ks_documents" action="update">
                <button
                  onClick={handleCreateRevision}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  {t("Ny revisjon")}
                </button>
              </RoleOnly>
            </div>
            <div className="border-t border-gray-200">
              {versions.length === 0 ? (
                <p className="px-4 py-5 text-sm text-gray-500">{t("Ingen versjoner")}</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {versions.map((version) => {
                    const isSelected = selectedVersionId === version.id;
                    return (
                    <li 
                      key={version.id} 
                      data-version-id={version.id} 
                      className={`px-4 py-4 transition-colors duration-200 cursor-pointer hover:bg-gray-50 ${
                        isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                      onClick={() => {
                        selectVersion(version);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {version.is_approved ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <ClockIcon className="h-5 w-5 text-yellow-500" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {t("Versjon")} {version.version}
                              {version.status === 'approved' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  {t("Godkjent")}
                                </span>
                              )}
                              {version.status === 'pending_approval' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {t("Venter på godkjenning")}
                                </span>
                              )}
                              {version.status === 'draft' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {t("Kladd")}
                                </span>
                              )}
                              {version.status === 'obsolete' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                  {t("Foreldet")}
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500">
                              {version.created_by?.name || t("Ukjent")} • {version.created_at && new Date(version.created_at).toLocaleString()}
                            </p>
                            {version.change_summary && (
                              <p className="text-sm text-gray-500 mt-1">{version.change_summary}</p>
                            )}
                            {version.approvals_count > 0 && (
                              <p className="text-xs text-gray-400 mt-1">
                                {version.approvals_count} {t("godkjenninger")}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {isSelected && (
                            <span className="text-xs text-blue-600 font-medium">{t("Vises")}</span>
                          )}
                          {canEdit && version.status === 'draft' && !version.is_approved && (
                            <>
                              {canCreateChecklist ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    selectVersion(version);
                                    handleGoToEditChecklist();
                                  }}
                                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  {t("quality.checklist.type.hse.edit_checklist")}
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/dashboard/hse-ks/documents/${id}/edit`);
                                  }}
                                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  {t("Rediger")}
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSubmitForApproval();
                                }}
                                className="text-sm text-green-600 hover:text-green-800 font-medium"
                              >
                                {t("Send til godkjenning")}
                              </button>
                            </>
                          )}
                          {canEdit && version.status === 'pending_approval' && !version.is_approved && (
                            canCreateChecklist ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectVersion(version);
                                  handleGoToEditChecklist();
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                              >
                                {t("quality.checklist.type.hse.edit_checklist")}
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/dashboard/hse-ks/documents/${id}/edit`);
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                              >
                                {t("Rediger")}
                              </button>
                            )
                          )}
                          {canApprove && !version.is_approved && version.status === 'pending_approval' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproveVersion(version.id);
                              }}
                              disabled={approvingVersion === version.id}
                              className="text-sm text-green-600 hover:text-green-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                              {approvingVersion === version.id ? t("Godkjenner...") : t("Godkjenn")}
                            </button>
                          )}
                          {/* Print button for any version */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrint(version.id);
                            }}
                            disabled={
                              (version.status !== 'approved' && !isAdmin) || 
                              printingVersionId === version.id
                            }
                            className="text-sm text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              (version.status !== 'approved' && !isAdmin)
                                ? t("Kun godkjente versjoner kan skrives ut")
                                : printingVersionId === version.id
                                ? t("Genererer PDF...")
                                : t("Skriv ut versjon")
                            }
                          >
                            {printingVersionId === version.id ? (
                              <svg className="animate-spin h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <PrinterIcon className="h-4 w-4 inline" />
                            )}
                          </button>
                        </div>
                      </div>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Access Dialog */}
      {isAdmin && (
        <DocumentAccessDialog
          documentId={id}
          documentTitle={document.title}
          open={showAccessDialog}
          onClose={() => setShowAccessDialog(false)}
          onSaved={() => refreshDocumentView()}
        />
      )}

      <ApproveRevisionDialog
        open={Boolean(approveDialogVersion)}
        versionNumber={approveDialogVersion?.version}
        submitting={Boolean(approvingVersion)}
        error={approveDialogError}
        onClose={() => setApproveDialogVersion(null)}
        onConfirm={(requiresReacknowledgement) =>
          approveVersionNow(approveDialogVersion.id, { requiresReacknowledgement })
        }
      />

      <NewDocumentFormDialog
        open={showFillFormDialog}
        onClose={() => setShowFillFormDialog(false)}
        defaultTemplateId={fillableTemplate?.id}
        defaultTitle={document.title}
      />

      <NewChecklistFromHseDialog
        open={showCreateChecklistDialog}
        onClose={() => setShowCreateChecklistDialog(false)}
        hseDocumentId={document?.id}
        defaultTitle={checklistTemplate?.title || document?.title}
      />
    </HsePageChrome>
  );
};

